// Servicio de autenticación conectado con FastAPI backend
import { API_BASE_URL } from '../config';
const TOKEN_KEY = 'control_campo_token';
const USER_KEY = 'control_campo_user';

export const authService = {
    // Login con el backend real
    login: async (username, password) => {
        try {
            // ✅ CAMBIO: Usar URLSearchParams en lugar de FormData
            const formData = new URLSearchParams();
            formData.append('username', username);
            formData.append('password', password);

            const response = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: formData
            });

            if (!response.ok) {
                let errorMessage = 'Credenciales incorrectas';
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData.detail || errorMessage;
                    } catch (e) {
                        // Ignorar fallo de parseo
                    }
                } else {
                    if (response.status === 504) {
                        errorMessage = 'El servidor tardó demasiado en responder (Timeout 504). Por favor, intenta de nuevo.';
                    } else if (response.status === 502) {
                        errorMessage = 'El servidor de base de datos o aplicación está temporalmente fuera de servicio (502).';
                    } else {
                        errorMessage = `Error del servidor (${response.status}). Intenta de nuevo más tarde.`;
                    }
                }
                throw new Error(errorMessage);
            }

            let tokenData;
            try {
                tokenData = await response.json();
            } catch (jsonErr) {
                throw new Error('Respuesta inválida del servidor. Intenta de nuevo.');
            }

            // Obtener perfil del usuario
            const profileResponse = await fetch(`${API_BASE_URL}/auth/profile`, {
                headers: {
                    'Authorization': `Bearer ${tokenData.access_token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!profileResponse.ok) {
                throw new Error('Error al obtener perfil del usuario');
            }

            const userProfile = await profileResponse.json();

            // Guardar en localStorage
            authService.setToken(tokenData.access_token);
            authService.setUser(userProfile);

            return {
                user: userProfile,
                token: tokenData.access_token
            };

        } catch (error) {
            console.error('Error en login:', error);
            
            // Detectar errores de red vs errores del servidor
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                throw new Error('No se pudo conectar con el servidor. Verifica tu conexión a internet e intenta de nuevo.');
            }
            
            // Timeout errors
            if (error.name === 'AbortError') {
                throw new Error('La conexión tardó demasiado. Verifica tu señal de internet e intenta de nuevo.');
            }
            
            // Network-related errors
            if (error.message && (error.message.includes('NetworkError') || error.message.includes('network'))) {
                throw new Error('Error de red. Asegúrate de tener buena señal de internet e intenta de nuevo.');
            }
            
            throw error;
        }
    },

    // Verificar token con el backend
    verifyToken: async (token) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 6000); // 6 segundos de timeout para campo

        try {
            if (!token) {
                token = localStorage.getItem(TOKEN_KEY);
            }

            if (!token) {
                throw new Error('No hay token disponible');
            }

            const response = await fetch(`${API_BASE_URL}/auth/profile`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const err = new Error('Token inválido');
                err.status = response.status;
                throw err;
            }

            const userProfile = await response.json();
            authService.setUser(userProfile);

            return userProfile;

        } catch (error) {
            clearTimeout(timeoutId);
            console.error('Error verificando token:', error);
            // Solo cerrar sesión si el error es explícito de credenciales inválidas (401 o 403)
            // o si definitivamente no hay token.
            if (error.status === 401 || error.status === 403 || error.message === 'No hay token disponible') {
                authService.logout();
            }
            throw error;
        }
    },

    // Obtener credenciales de prueba del backend
    getTestCredentials: async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/test-login`);
            if (!response.ok) {
                throw new Error('Error al obtener credenciales de prueba');
            }
            return await response.json();
        } catch (error) {
            console.error('Error obteniendo credenciales de prueba:', error);
            // ✅ CAMBIO: Contraseñas actualizadas con las correctas
            return {
                message: "Credenciales de ejemplo para probar login",
                examples: [
                    {
                        username: "SOLMTCMXT0166",
                        password: "SOLMTCMXT0166XH01",
                        full_name: "JEHOVA GUSTAVO IBARRA ZUNIGA",
                        region: "METRO SUR ORIENTE",
                        distrito: "AEROPUERTO"
                    },
                    {
                        username: "GBRMTAGST0100",
                        password: "GBRMTAGST0100LR08",
                        full_name: "FERNANDO BARRAGAN ALDAPE",
                        region: "OCCIDENTE",
                        distrito: "AGUASCALIENTES"
                    },
                    {
                        username: "GBRMTAGST0101",
                        password: "GBRMTAGST0101NC04",
                        full_name: "RICARDO MACIAS SANCHEZ",
                        region: "OCCIDENTE",
                        distrito: "AGUASCALIENTES"
                    }
                ]
            };
        }
    },

    // Funciones de almacenamiento local
    setToken: (token) => {
        localStorage.setItem(TOKEN_KEY, token);
    },

    getToken: () => {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem(TOKEN_KEY);
        }
        return null;
    },

    setUser: (user) => {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    },

    getUser: () => {
        const user = localStorage.getItem(USER_KEY);
        return user ? JSON.parse(user) : null;
    },

    logout: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    },

    isAuthenticated: () => {
        return !!localStorage.getItem(TOKEN_KEY);
    },

    // Funciones adicionales para el frontend
    getCurrentUser: () => {
        return authService.getUser();
    },

    getCurrentToken: () => {
        return localStorage.getItem("control_campo_token");
    },

    // Función para refrescar datos del usuario
    refreshUserData: async () => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
            return await authService.verifyToken(token);
        }
        throw new Error('No hay sesión activa');
    }
};